<script lang="ts">
	import { _ } from 'svelte-i18n';
	import Icon from '$lib/components/Icon/Icon.svelte';
	import Button from '$lib/components/Button/Button.svelte';
	import { clickOutside } from '$lib/actions/clickOutside';
	import FileUploader from '$lib/components/FileUploader/FileUploader.svelte';
	import { fileUploadStore } from '$lib/components/FileUploader/fileUploadStore';
	import Spinner from '$lib/components/Spinner/Spinner.svelte';
	import { getHubStores } from '$lib/stores/hubStores';
	import { toastMessages } from '$lib/stores/toastMessageStore';
	import { previewImages } from '$textEditor/PostComposer/composerStore';
	import { onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { PostParent } from '$lib/api/restApi';

	export let renderClearButton = true;
	export let post: Writable<PostParent>;

	const { hub, restApi } = getHubStores();

	let menuOpen = false;
	let tempSelectedImage = '';

	let images: string[] = [];

	const toggleMenu = () => {
		menuOpen = !menuOpen;
		if (menuOpen) {
			tempSelectedImage = $post.title_image.url || '';
			images = $previewImages;
		}
	};

	const setTempSelectedImage = (image: string) => {
		tempSelectedImage = image;
	};

	const save = () => {
		$post.title_image.url = tempSelectedImage;
		if (!$previewImages.includes(tempSelectedImage)) {
			$previewImages = [...new Set([tempSelectedImage, ...$previewImages])];
		}
		menuOpen = false;
	};

	let uploading = false;

	const addImage = async () => {
		uploading = true;
		const file = $fileUploadStore[$fileUploadStore.length - 1];
		$fileUploadStore = [];

		const response = await restApi.uploadFile($hub.id, file);
		if (response.error) {
			toastMessages.danger('', response.error);
		} else {
			if (response.data) {
				const imageUrl = response.data.image.url;
				images = [imageUrl, ...$previewImages];
				setTempSelectedImage(imageUrl);
			}
			uploading = false;
		}
	};

	onDestroy(() => {
		$previewImages = [];
		tempSelectedImage = '';
	});
</script>

<div>
	<div id="container">
		{#if menuOpen}
			<div id="menu" use:clickOutside={() => (menuOpen = false)}>
				<header>
					{$_('title.image.editor.header')}
				</header>
				<!-- svelte-ignore a11y-missing-attribute -->
				<div class="selectedImageContainer">
					{#if uploading}
						<Spinner />
					{:else if !tempSelectedImage}
						<Icon icon="no-image" size="100" color="grey" />
					{:else}
						<img src={tempSelectedImage} width="200" height="200" />
					{/if}
				</div>
				<div class="images scrollX">
					{#each images as image}
						<button class="image" type="button" on:click={() => setTempSelectedImage(image)}>
							<!-- svelte-ignore a11y-missing-attribute -->
							<img
								class:selectedSmallImage={image == tempSelectedImage}
								src={image}
								width="50"
								height="50"
							/>
						</button>
					{/each}
				</div>
				<footer>
					<div class="footerLeft">
						<FileUploader
							on:fileUploaded={() => addImage()}
							accept=".jpg, .jpeg, .gif, .png, .webp"
							multiple={false}
						>
							<button type="button">
								{$_('common.upload')}
							</button>
						</FileUploader>
						{#if renderClearButton}
							<button
								type="button"
								on:click={() => {
									$post.title_image.url = '';
									tempSelectedImage = '';
									menuOpen = false;
								}}
							>
								{$_('common.clear')}
							</button>
						{/if}
					</div>
					<Button on:click={save}>{$_('common.save')}</Button>
				</footer>
			</div>
		{/if}
	</div>
</div>

<button
	type="button"
	class="cameraButton"
	on:click={toggleMenu}
	aria-label={$_('title.image.editor.header')}
>
	{#if $post.title_image?.url == ''}
		<Icon icon="no-image" size="25" color="grey" clickable />
		<Icon icon="dropdown-arrow" size="25" color="grey" clickable />
	{:else}
		<div class="buttonImage" style={`background-image: url(${$post.title_image?.url})`} />
	{/if}
</button>

<style>
	header {
		display: flex;
		margin-block-end: var(--gutterSmall);
		padding: var(--gutterSmall);
		border-block-end: solid 1px var(--grayLight);
	}

	.selectedImageContainer {
		display: flex;
		align-items: center;
		justify-content: center;
		min-block-size: 210px;
		padding-block-end: 10px;
		border-block-end: solid 1px var(--grayLight);
	}

	.images {
		display: flex;
		gap: 5px;
		block-size: 55px;
		margin-block: var(--gutterSmaller);
	}

	.selectedSmallImage {
		border: 2px solid var(--grayDarkest);
	}

	footer {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: space-between;
		padding: var(--gutterSmaller);
		border-block-start: solid 1px var(--grayLight);
		overflow: hidden;
	}

	.footerLeft {
		display: flex;
	}

	button {
		inline-size: 60px;
		border: none;
		background: none;
		cursor: pointer;
	}

	.footerLeft button {
		inline-size: auto;
	}

	.cameraButton {
		border: 1px solid var(--grayLight);
		border-radius: var(--gutterSmaller);
		white-space: nowrap;
	}

	.buttonImage {
		block-size: 100%;
		inline-size: 100%;
		background-position: center;
		background-size: cover;
	}

	#container {
		position: relative;
		inset-block-end: 5px;
	}

	#menu {
		position: absolute;
		inline-size: 300px;
		border-radius: var(--gutterSmaller);
		background-color: var(--grayLightest);
		box-shadow: rgb(0 0 0 / 20%) 0 0 18px 0;
		inset-block-end: 0;
	}
</style>
